import { useRef, useCallback, useState } from "react";
import { toast } from "sonner";
import { sanitizeFileName } from "@/lib/fileNames";

/**
 * Drives `workers/zipExportWorker.js`: streams a set of remote files into a ZIP
 * off the main thread and hands the result to the browser as a download.
 *
 * Source-agnostic — callers pass pre-resolved groups of `{ url, fileName }`.
 * See `useAssetsDownload` for the asset bulk-download caller.
 */
export default function useZipExport() {
  const workerRef = useRef(null);
  const toastIdRef = useRef(null);
  const lastToastUpdateRef = useRef(0);
  const [exporting, setExporting] = useState(false);

  const teardown = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  // Deliberately not torn down on unmount: the worker and its message handler
  // only touch globals, so an export survives navigating away and still hands
  // the finished zip to the browser.

  /**
   * @param groups     [{ path: string[], items: [{ url, fileName }] }]
   * @param rootFolder folder to nest everything under inside the zip (optional)
   * @param zipName    file name for the download, without the .zip extension
   * @param extraFiles [{ name, text }] — files the caller already holds in
   *                   memory, written to the archive root without a fetch
   */
  const startExport = useCallback(
    ({ groups, rootFolder = null, zipName, extraFiles = [] }) => {
      const totalFiles =
        (groups || []).reduce((sum, g) => sum + g.items.length, 0) +
        extraFiles.length;

      if (totalFiles === 0) {
        toast.error("No downloadable files found.");
        return;
      }

      setExporting(true);

      toastIdRef.current = toast.loading(
        `Preparing export... (0/${totalFiles})`,
        {
          description: "Downloading files...",
          duration: Infinity,
        },
      );

      // Terminate any existing worker
      teardown();

      const worker = new Worker(
        new URL("../workers/zipExportWorker.js", import.meta.url),
        { type: "module" },
      );
      workerRef.current = worker;

      worker.onmessage = (event) => {
        const { type, current, total, message, blob } = event.data;

        if (type === "progress") {
          // Throttle toast updates to avoid React max-update-depth errors
          const now = Date.now();
          if (now - lastToastUpdateRef.current >= 500) {
            lastToastUpdateRef.current = now;
            toast.loading(`Exporting... (${current}/${total})`, {
              id: toastIdRef.current,
              description: message,
              duration: Infinity,
            });
          }
        } else if (type === "done") {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          // The zip name is derived from folder/show names, so it needs the
          // same scrubbing as the entries inside the archive. Paths inside
          // the zip are sanitized by the worker itself.
          a.download = `${sanitizeFileName(zipName, "export")}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          toast.success("Export complete!", {
            id: toastIdRef.current,
            description: `${totalFiles} file${totalFiles === 1 ? "" : "s"} downloaded.`,
            duration: 4000,
          });

          setExporting(false);
          teardown();
        } else if (type === "error") {
          toast.error("Export failed", {
            id: toastIdRef.current,
            description: message,
            duration: 5000,
          });
          setExporting(false);
          teardown();
        }
      };

      worker.onerror = (err) => {
        toast.error("Export failed", {
          id: toastIdRef.current,
          description: err.message || "An unexpected error occurred.",
          duration: 5000,
        });
        setExporting(false);
        teardown();
      };

      worker.postMessage({ groups, rootFolder, extraFiles });
    },
    [teardown],
  );

  const cancelExport = useCallback(() => {
    teardown();
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
    setExporting(false);
  }, [teardown]);

  return { startExport, cancelExport, exporting };
}
